"use client";

import { useEffect, useState } from "react";

import { cn } from "@rivval/css";

interface ConnectLoaderStatusProps
  extends React.HTMLAttributes<HTMLDivElement> {
  segments?: number;
  text?: string;
}

export function ConnectLoaderStatus({
  segments = 12,
  text = "SYSTEM ACTIVE",
  className,
  ...props
}: ConnectLoaderStatusProps) {
  const [activeSegments, setActiveSegments] = useState<number[]>([]);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSegments((prev) => {
        const next = [...prev];
        if (next.length >= segments) {
          next.shift();
        }
        if (next.length < segments) {
          next.push(Math.floor(Math.random() * segments));
        }
        return next;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [segments]);

  return (
    <div
      className={cn("flex flex-col items-center gap-2", className)}
      {...props}
    >
      <div className="relative flex w-full items-center gap-1">
        {/* Left decorative elements */}
        <div className="flex items-center gap-1">
          <div className="h-px w-4 bg-foreground" />
          <div className="text-foreground">+</div>
          <div className="h-px w-2 bg-foreground" />
        </div>

        {/* Segments container */}
        <div className="flex flex-1 items-center justify-center gap-1">
          {Array.from({ length: segments }).map((_, i) => (
            <div
              key={i}
              className={cn(
                "h-8 w-2 rounded-sm transition-all duration-200",
                "border border-foreground/20 bg-foreground/50",
                activeSegments.includes(i) &&
                  "bg-foreground shadow-[0_0_8px_rgba(239,68,68,0.6)]",
              )}
            >
              <div
                className={cn(
                  "h-full w-full opacity-0 transition-opacity duration-200",
                  activeSegments.includes(i) && "opacity-100",
                )}
                style={{
                  backgroundImage:
                    "linear-gradient(0deg, transparent 0%, rgba(239,68,68,0.2) 50%, transparent 100%)",
                  animation: activeSegments.includes(i)
                    ? "scanline 1s linear infinite"
                    : "none",
                }}
              />
            </div>
          ))}
        </div>

        {/* Right decorative elements */}
        <div className="flex items-center gap-1">
          <div className="h-px w-2 bg-foreground" />
          <div className="text-foreground">+</div>
          <div className="h-px w-4 bg-foreground" />
        </div>
      </div>

      {/* Text below */}
      <div className="flex items-center gap-2 text-xs tracking-[0.2em] text-foreground">
        <span className="h-px w-12 bg-foreground/50" />
        <span className="animate-pulse">{text}</span>
        <span className="h-px w-12 bg-foreground/50" />
      </div>
    </div>
  );
}
